import { type FC, useEffect } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@r/components/ui/form.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useApiAdminlocalesIdPatch } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import type {
  LocaleLocaleAdminReadModel,
  LocaleLocaleWriteModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface AdminLocaleToEditFormProps {
  locale: LocaleLocaleAdminReadModel;
  isSuperAdmin: boolean;
  onSuccess: () => void;
}

export const AdminLocaleToEditForm: FC<AdminLocaleToEditFormProps> = ({
  locale,
  isSuperAdmin,
  onSuccess
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const editLocaleFormSchema = z.object({
    content: z.string().min(1, {
      message: t('admin.translations.edit.form.content.error')
    })
  });

  const editLocaleForm = useForm<z.infer<typeof editLocaleFormSchema>>({
    resolver: zodResolver(editLocaleFormSchema),
    defaultValues: {
      content: locale.content || ''
    }
  });

  const editLocaleMutation = useApiAdminlocalesIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.translations.edit.form.success'));
        onSuccess();
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('admin.translations.edit.form.error'));
      },
      networkMode: 'always'
    }
  });

  const handleSubmit = (data: z.infer<typeof editLocaleFormSchema>) => {
    if (!locale.id) {
      throw new Error('Locale ID not found');
    }
    const body: LocaleLocaleWriteModel = {
      label: locale.label ?? '',
      content: data.content,
      country: '/countries/FRA',
      isPublic: true,
      isEditable: isSuperAdmin
    };
    editLocaleMutation.mutate({ id: String(locale.id), data: body });
  };

  useEffect(() => {
    if (locale) {
      editLocaleForm.reset({
        content: locale.content || ''
      });
    }
  }, [locale, editLocaleForm.reset]);

  return (
    <div>
      <h3 className="mb-4 text-lg font-medium">{t('admin.translations.edit.default.title')}</h3>

      <Form {...editLocaleForm}>
        <form onSubmit={editLocaleForm.handleSubmit(handleSubmit)} className="space-y-4">
          <div>
            <FormLabel className="text-sm font-medium">
              {t('admin.translations.edit.form.label')}
            </FormLabel>
            <Input disabled value={locale.label || ''} className="mt-1" readOnly />
          </div>

          <div>
            <FormLabel className="text-sm font-medium">
              {t('admin.translations.edit.form.current.content')}
            </FormLabel>
            <Input disabled value={locale.content || ''} className="mt-1 bg-muted" readOnly />
          </div>

          <FormField
            control={editLocaleForm.control}
            name="content"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.translations.edit.form.new.content')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('admin.translations.edit.form.content.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            loading={editLocaleMutation.isPending}
            className="w-full"
            type="submit"
            disabled={!editLocaleForm.formState.isDirty || editLocaleMutation.isPending}
          >
            {t('admin.translations.edit.default.button')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
